import React, { useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import GroupChannel from '@sendbird/uikit-react/GroupChannel';
import type { User } from '@sendbird/chat';
import type { Member } from '@sendbird/chat/groupChannel';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import Message from '../Message';
import ChatHeader from './ChatHeader';
import EmptyChatIcon from './EmptyChatIcon';
import EmptyPanel from './EmptyPanel';
import UserProfileCard from './UserProfileCard';
import MentionSuggestionItem from './MentionSuggestionItem';
import { EmptyChatIconBadge } from './StyledComponents';
import { targetMessageIdSelector, targetTimestampSelector } from 'domain/chat';
import { useChatUserLookup } from '../../hooks/useChatUserDirectory';

interface Props {
  channelUrl: string;
  onToggleSettings: (v: boolean) => void;
  onClose: () => void;
  onBackToList?: () => void;
  /** Mobile view — disables new-tab navigations in the chat header. */
  isMobile?: boolean;
}

const ChatPanel: React.FC<Props> = ({ channelUrl, onToggleSettings, onClose, onBackToList, isMobile = false }) => {
  const targetTimestamp = useSelector(targetTimestampSelector);
  const targetMessageId = useSelector(targetMessageIdSelector);
  const mentionLookup = useChatUserLookup();
  // MUST keep a stable identity (DA-19882). GroupChannelManager puts this prop into its
  // `renderProps` memo, which is a dependency of the useDeepCompareEffect that writes
  // `currentChannel` back into the provider store. Deep-compare falls back to reference
  // equality for functions, so an inline arrow makes that effect fire on every render —
  // and a render still in flight while getChannel() is pending then flushes its stale
  // `currentChannel: null` over the channel that was just fetched, leaving the pane
  // stuck on `loading: true` with no message request ever issued.
  const renderUserMentionItem = useCallback(
    ({ user }: { user: User | Member }) => <MentionSuggestionItem user={user} lookup={mentionLookup} />,
    [mentionLookup],
  );
  const { formatMessage } = useIntl();

  const renderPlaceholderEmpty = () => (
    <Stack flex={1} alignItems="center" justifyContent="center" spacing={2.5}>
      <EmptyChatIconBadge>
        <EmptyChatIcon sx={{ fontSize: 64 }} />
      </EmptyChatIconBadge>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {formatMessage({ id: 'chat.panel.noMessages', defaultMessage: 'No messages' })}
      </Typography>
    </Stack>
  );

  return (
    <Stack flex={1} direction="column">
      {channelUrl ? (
        <GroupChannel
          key={channelUrl}
          channelUrl={channelUrl}
          startingPoint={targetTimestamp}
          animatedMessageId={targetMessageId}
          disableUserProfile={false}
          renderUserProfile={({ user, close }) => <UserProfileCard user={user} onClose={close} />}
          renderChannelHeader={() => (
            <ChatHeader
              onToggleSettings={onToggleSettings}
              onClose={onClose}
              onBackToList={onBackToList}
              isMobile={isMobile}
            />
          )}
          renderMessageContent={(props) => <Message {...props} />}
          renderUserMentionItem={renderUserMentionItem}
          renderPlaceholderEmpty={renderPlaceholderEmpty}
        />
      ) : (
        <EmptyPanel onClose={onClose} />
      )}
    </Stack>
  );
};

export default ChatPanel;
